const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

const getPayloadData = (payload) => {
  let current = payload;

  // Axios responses and a few older API versions add one or two `data` wrappers.
  for (let depth = 0; depth < 3; depth += 1) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) break;
    if (!current.data || typeof current.data !== 'object') break;
    current = current.data;
  }

  return current && typeof current === 'object' ? current : {};
};

const findObjectValue = (object, wantedKey) => {
  if (!object || typeof object !== 'object') return undefined;
  const matchingKey = Object.keys(object).find((key) => normalizeKey(key) === normalizeKey(wantedKey));
  return matchingKey === undefined ? undefined : object[matchingKey];
};

export const normalizeDeviceChartResponse = (payload, curves = [], deviceDid = '') => {
  const data = getPayloadData(payload);
  const timestamps = findObjectValue(data, 't');
  const xData = Array.isArray(timestamps) ? timestamps : [];
  const nestedDeviceData = findObjectValue(data, deviceDid);
  const seriesContainer = nestedDeviceData && typeof nestedDeviceData === 'object'
    ? nestedDeviceData
    : data;

  const seriesByPin = {};
  (Array.isArray(curves) ? curves : []).forEach((curve) => {
    const pin = String(curve?.pin ?? '').trim();
    if (!pin || normalizeKey(pin) === 'pin') return;

    let values = findObjectValue(seriesContainer, pin);
    if (!Array.isArray(values) && (curves || []).length === 1) {
      const fallbackKey = Object.keys(seriesContainer).find(
        (key) => normalizeKey(key) !== 't' && Array.isArray(seriesContainer[key])
      );
      values = fallbackKey === undefined ? undefined : seriesContainer[fallbackKey];
    }

    seriesByPin[pin] = Array.isArray(values) ? values : [];
  });

  return { xData, seriesByPin };
};

export const normalizeDeviceLivePayload = (payload, deviceDid = '') => {
  if (!payload || typeof payload !== 'object') return {};

  if (Array.isArray(payload)) {
    return payload.reduce((result, item) => {
      if (!item || typeof item !== 'object') return result;
      const pin = item.pin ?? item.f ?? item.field ?? item._field;
      const value = item.value ?? item.v ?? item._value;
      if (pin !== undefined && value !== undefined) result[String(pin).trim()] = value;
      return result;
    }, {});
  }

  const candidate = payload.latestStreamData
    ?? payload.pins
    ?? payload.values
    ?? payload.streamData
    ?? payload.data
    ?? payload;

  if (Array.isArray(candidate)) return normalizeDeviceLivePayload(candidate, deviceDid);
  if (!candidate || typeof candidate !== 'object') return {};

  const nestedDeviceData = findObjectValue(candidate, deviceDid);
  if (nestedDeviceData && typeof nestedDeviceData === 'object') {
    return normalizeDeviceLivePayload(nestedDeviceData, '');
  }

  return Object.entries(candidate).reduce((result, [pin, rawValue]) => {
    if (['did', 'device', 'status', 'timestamp', 'time', 't'].includes(normalizeKey(pin))) return result;
    const value = rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)
      ? (rawValue.value ?? rawValue.v ?? rawValue._value)
      : rawValue;
    if (value !== undefined && (value === null || typeof value !== 'object')) {
      result[String(pin).trim()] = value;
    }
    return result;
  }, {});
};
